import type { FormProduct } from "@shared/types";
import { isIntensityPosition } from "@shared/types/intensity";
import { handleDisplayVariantDrafts } from "./handle-display-variant-drafts";

/**
 * True when the draft carries the fields this feature saves on every write.
 * `normaliseIntensityFields` (in `product-form.utils.ts`) always writes
 * `intensityPosition` as an explicit `null`/position — never `undefined` —
 * once a draft has gone through the current form, so its presence is what
 * distinguishes "current-generation draft, trust it outright" from "legacy
 * draft predating these fields, fall back per-field."
 */
function hasIntensitySnapshot(draft: FormProduct): boolean {
  return "intensityPosition" in draft;
}

type DraftIntensityFields = Pick<FormProduct, "intensityPosition" | "intensityZone" | "closestMatchCoffee">;

/**
 * A current-generation draft is trusted outright, including an explicit clear
 * (`intensityPosition: null`) — falling back to the product's value whenever a
 * field is unset would resurrect a score the roaster deliberately removed,
 * since JSON persistence can't tell "cleared" apart from "absent" any other
 * way. A legacy draft, which never wrote these fields at all, still falls
 * back per-field.
 *
 * The "most similar to" pick is checked on its own key, not assumed to arrive
 * with the position/zone pair: a draft saved before the pick existed already
 * carries `intensityPosition` but never wrote it, and copying its absence over
 * the product's pick would silently drop it. A key the form did write (even as
 * `null`) is trusted outright, so an explicit clear survives. When the key is
 * absent, the product's pick is only reused if the draft kept the same
 * position — a pick belongs to the position it explains, so it must not follow
 * the roaster to a different one.
 */
function computeDraftIntensity(product: FormProduct, draft: FormProduct): DraftIntensityFields {
  if (hasIntensitySnapshot(draft)) {
    return {
      intensityPosition: draft.intensityPosition,
      intensityZone: draft.intensityZone,
      closestMatchCoffee: resolveDraftPick(product, draft),
    };
  }

  return {
    intensityPosition: draft.intensityPosition ?? product.intensityPosition,
    intensityZone: draft.intensityZone ?? product.intensityZone,
    closestMatchCoffee: draft.closestMatchCoffee ?? product.closestMatchCoffee,
  };
}

function resolveDraftPick(product: FormProduct, draft: FormProduct): FormProduct["closestMatchCoffee"] {
  if ("closestMatchCoffee" in draft) return draft.closestMatchCoffee;
  if (draft.intensityPosition === product.intensityPosition) return product.closestMatchCoffee;
  return undefined;
}

/**
 * Merges a product with its draft data based on import status.
 *
 * Logic copied from client ProductSelectModal.tsx lines 178-197.
 *
 * Merge strategy:
 * - If product is imported (has localVariants): use product fields, not draft.
 *   The coffee is on PIM, so its intensity is locked (scored or not) and the
 *   product's own value always wins over any draft.
 * - If product is not imported: use draft fields
 * - Preserve product's imageUploads; fall back to draft imageUploads when
 *   the product has none (so draft images are not lost for products
 *   without a PIM image)
 * - Merge variants via handleDisplayVariantDrafts
 * - Set hasDraft based on import status or draft.hasVariantsDraft
 * - Reset isProductFormValid to false (will be recalculated by validation)
 *
 * @param product - Original product
 * @param draftProducts - Array of draft products to search for matching draft
 * @returns Merged product with draft data
 */
export function handleDisplayDraft(product: FormProduct, draftProducts: FormProduct[]): FormProduct {
  const isProductImportedLocal = !!product.localVariants?.length;

  // Prefer the explicit signal an upstream merge (`mapToLocalProduct`,
  // `enrichWithPimData`) already computed — it knows whether the position
  // came from an actual PIM approval or is just a Salesforce provisional
  // value that happens to be non-null, which this file cannot tell apart on
  // its own. Only recompute for products that never went through that merge
  // (e.g. the ShipStation "New Bag Size" flow's PIM-only `existingProduct`).
  const hasApprovedIntensity = product.hasApprovedIntensity ?? (isProductImportedLocal && isIntensityPosition(product.intensityPosition));

  const draft = draftProducts.find((draft) => draft.id === product.id);
  if (!draft) return { ...product, hasApprovedIntensity };

  // The intensity score (and its closest-match pick) stays editable only for
  // a coffee that is not on PIM yet — taking the product's value here would
  // discard an intensity-only edit the moment the modal reopened. Once the
  // coffee is on PIM, intensity is locked and the product's own value must
  // always win (scored or not), or reopening an old draft captured before
  // that point (autosaved on any unrelated field change, not just an
  // intensity edit) would silently overwrite it with a stale snapshot.
  const isIntensityStillEditable = !hasApprovedIntensity && !isProductImportedLocal;
  const draftIntensity = isIntensityStillEditable ? computeDraftIntensity(product, draft) : {};

  // An approved score must win even when the draft is the merge base (a
  // product with no `localVariants`, e.g. approved in Salesforce before PIM
  // has it): otherwise the locked section would show the draft's stale value.
  const approvedIntensity: Partial<DraftIntensityFields> = hasApprovedIntensity
    ? {
        intensityPosition: product.intensityPosition,
        intensityZone: product.intensityZone,
        closestMatchCoffee: product.closestMatchCoffee,
      }
    : {};

  const merged = {
    ...(isProductImportedLocal ? product : draft),
    ...draftIntensity,
    ...approvedIntensity,
    hasApprovedIntensity,
    imageUploads: product.imageUploads?.length ? product.imageUploads : (draft.imageUploads ?? product.imageUploads),
    variants: handleDisplayVariantDrafts(product.variants, draft.variants),
    hasDraft: !isProductImportedLocal || !!draft.hasVariantsDraft,
  };

  return {
    ...merged,
    isProductFormValid: false, // Will be recalculated by validation
  };
}
